import { Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { Location } from "@angular/common";

import { catchError, finalize, tap } from "rxjs/operators";
import { of } from "rxjs";

import { ToastService } from "@dffedb/ui";
import { SvgIconRegistry } from "@dffedb/util";
import { LoginService } from "@e-forsyning/common/auth";
import { RedirectService } from "@e-forsyning/common/router";
import { mdiArrowLeft } from "@mdi/js";

import { LoginContainerComponent } from "../login-container/login-container.component";
import {
    evaluatePasswordRequirements,
    isPasswordValid,
    PasswordRequirementState,
    passwordsMatch
} from "../tekniker-glemt-kodeord/password-rules";
import { MatFormField } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";

@Component({
    selector: "tekniker-skift-kodeord",
    templateUrl: "./tekniker-skift-kodeord.component.html",
    styleUrls: ["./tekniker-skift-kodeord.component.scss"],
    imports: [LoginContainerComponent, FormsModule, ReactiveFormsModule, MatFormField, MatInput, MatButton, MatIconButton, MatIcon]
})
export class TeknikerSkiftKodeordComponent implements OnInit {
    public form: FormGroup;
    public arbejder = false;
    public fejlBesked = "";
    public showUnmetAsError = false;
    public requirements: PasswordRequirementState[] = evaluatePasswordRequirements("", false);
    /** Tvungen migrering efter login. Frivillig (Brugerinfo): kan forlade uden at gemme. */
    public isForced = false;

    private readonly destroyRef = inject(DestroyRef);
    private readonly formBuilder = inject(FormBuilder);
    private readonly loginService = inject(LoginService);
    private readonly toastService = inject(ToastService);
    private readonly redirectService = inject(RedirectService);
    private readonly location = inject(Location);
    private readonly svgIconRegistry = inject(SvgIconRegistry);

    constructor() {
        this.svgIconRegistry.registerIcon(mdiArrowLeft, "arrow-left");
    }

    public ngOnInit(): void {
        this.isForced = this.loginService.skalSkifteKodeord();
        this.form = this.formBuilder.group({
            nytKodeord: ["", Validators.required],
            gentagNytKodeord: ["", Validators.required]
        });

        this.form
            .get("nytKodeord")
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value: string) => {
                this.requirements = evaluatePasswordRequirements(value || "", this.showUnmetAsError);
            });

        if (!this.loginService.erLoggetInd()) {
            this.fejlBesked = "Du er ikke logget ind. Log ind igen for at skifte adgangskode.";
        }
    }

    public get kanGemme(): boolean {
        const password = this.form?.get("nytKodeord")?.value || "";
        const repeat = this.form?.get("gentagNytKodeord")?.value || "";
        return !this.arbejder && this.loginService.erLoggetInd() && isPasswordValid(password) && passwordsMatch(password, repeat);
    }

    public onPasswordBlur(): void {
        this.showUnmetAsError = true;
        const password = this.form.get("nytKodeord")?.value || "";
        this.requirements = evaluatePasswordRequirements(password, true);
    }

    public tilbage(): void {
        if (this.isForced) {
            this.loginService.logout();
            this.redirectService.redirectTilLogin();
            return;
        }
        if (window.history.length > 1) {
            this.location.back();
        } else {
            this.redirectService.redirectTilDefaultLanding();
        }
    }

    public gemAdgangskode(): void {
        if (!this.kanGemme) {
            return;
        }

        this.arbejder = true;
        this.fejlBesked = "";
        const password = this.form.get("nytKodeord")?.value;

        this.loginService
            .teknikerSkiftPwd(password)
            .pipe(
                tap(() => {
                    this.toastService.visInfo("Dit kodeord er nu ændret.");
                    this.redirectService.redirectTilDefaultLanding();
                }),
                catchError((error: any) => {
                    this.fejlBesked =
                        error?.friendlyMessage ||
                        error?.message ||
                        "Kunne ikke gemme adgangskoden. Prøv igen, eller brug glemt-adgangskode hvis problemet fortsætter.";
                    return of(null);
                }),
                finalize(() => {
                    this.arbejder = false;
                })
            )
            .subscribe();
    }
}
